import type { CalculatorOutput, CalculatorInputs } from '@/types/calculator';
import '@/types/gtag';
import { hasAnalyticsConsent } from '@/utils/consentUtils';
import { isProd } from '@/utils/envUtils';

const trackEvent = (eventName: string, parameters?: Record<string, unknown>) => {
  if (hasAnalyticsConsent()) {
    if (!isProd()) {
      console.log('Tracking event:', eventName, parameters);
    }
    window.gtag('event', eventName, parameters);
  }
};

export const trackCalculatorResult = (
  result: CalculatorOutput,
  inputs: CalculatorInputs
) => {
  const currentTotal = inputs.netRent + inputs.livingCosts;
  const estimatedTotal = result.estimation.high;

  const resultBucket = estimatedTotal > currentTotal ? 'increase' : 'decrease';

  trackEvent('rent_adjustment_calculator_result', {
    result: resultBucket
  });
};

export const trackCalculatorValidationError = (missingFields: number) => {
  trackEvent('rent_adjustment_calculator_validation_error', {
    missing_fields: missingFields
  });
};

export const trackCalculatorError = (errorType: string = 'calculation_failed') => {
  trackEvent('rent_adjustment_calculator_error', {
    error_type: errorType
  });
};

export const trackCalculatorSubmit = () => {
  trackEvent('rent_adjustment_calculator_submit');
};

export const trackLocaleChange = (originalLocale: string, targetLocale: string) => {
  trackEvent('locale_change', {
    original: originalLocale,
    target: targetLocale
  });
};

export const trackTooltipHover = (tooltipContent: string, location: string) => {
  trackEvent('tooltip_hover', {
    content: tooltipContent,
    location: location
  });
};

export const trackInternalNavigation = (
  fromPath: string,
  toPath: string,
  linkType: 'header' | 'cta' | 'footer'
) => {
  trackEvent('internal_navigation', {
    from_path: fromPath,
    to_path: toPath,
    link_type: linkType,
  });
};

export const trackLetterEditorDownload = (language: string) => {
  trackEvent('letter_editor_download', {
    language: language
  });
};

export const trackLetterEditorPrint = (language: string) => {
  trackEvent('letter_editor_print', {
    language: language
  });
};

export const trackLetterEditorCopy = (language: string) => {
  trackEvent('letter_editor_copy', {
    language: language
  });
};

export const trackLetterEditorEdit = (language: string) => {
  trackEvent('letter_editor_edit', {
    language: language
  });
};

export const trackLetterEditorEditApply = (language: string) => {
  trackEvent('letter_editor_edit_apply', {
    language: language
  });
};

export const trackLetterEditorEditReset = (language: string) => {
  trackEvent('letter_editor_edit_reset', {
    language: language
  });
};